// v4 smelter — a stout stone furnace with a slate (ROOF_MINERAL) cap, a tall
// dark flue trailing a pale smoke puff, a glowing FIRE mouth on the open
// forecourt, a fresh ingot stack cooling beside it and an ore heap waiting in
// the yard.
//
// Ported 1:1 from age-of-mcp's v4/smelter.ts, using the library's shared
// v3 style helpers (`styleV3`).
import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, poles, slab, CANVAS, FIRE, ROOF_MINERAL, STEEL, STONE, STONE_DK } = styleV3;

const BASE = 0.12;            // foundation slab top
const BODY_H = 0.66;          // furnace body height
const TOP = BASE + BODY_H;    // furnace top

// The stout stone furnace body, `h` tall on the foundation.
function furnace(h: number): Part {
  return box(0.62, h, 0.62, STONE, [0, BASE + h / 2, 0]);
}

// The slate cap — the mineral-industry roof code (stage 3 only).
function cap(): Part {
  return box(0.7, 0.09, 0.7, ROOF_MINERAL, [0, TOP + 0.045, 0]);
}

// The tall dark flue with its pale smoke puff — the signature vertical.
function flue(): Part[] {
  return [
    box(0.2, 0.66, 0.2, STONE_DK, [-0.17, TOP + 0.36, -0.17]),
    box(0.17, 0.13, 0.17, CANVAS, [-0.17, TOP + 0.76, -0.17]),
  ];
}

// The glowing fire mouth on the front: dark arch + fire glow.
function mouth(): Part[] {
  return [
    box(0.3, 0.26, 0.06, STONE_DK, [0, BASE + 0.22, 0.32]),
    box(0.22, 0.18, 0.03, FIRE,    [0, BASE + 0.2, 0.335]),
  ];
}

// Fresh steel ingots cooling in the front yard.
function ingots(): Part[] {
  return [
    box(0.17, 0.05, 0.09, STEEL, [0.3, BASE + 0.025, 0.42]),
    box(0.17, 0.05, 0.09, STEEL, [0.3, BASE + 0.075, 0.42], [0, 0.5, 0]),
    box(0.17, 0.05, 0.09, STEEL, [0.12, BASE + 0.025, 0.46], [0, -0.2, 0]),
  ];
}

// The ore heap waiting beside the furnace.
function oreHeap(): Part {
  return cyl(0.02, 0.18, 0.18, 7, STONE_DK, [-0.3, BASE + 0.09, 0.38]);
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.88, 0.88)];
  if (s === 1) return [slab(0.88, 0.88), furnace(0.3), ...poles(0.82, 0.82, 0.6)];
  if (s === 2) {
    return [slab(0.88, 0.88), furnace(BODY_H), ...mouth(), ...flue(), ...poles(0.82, 0.82, 1.1)];
  }
  return [
    slab(0.88, 0.88), furnace(BODY_H), cap(), ...flue(),
    ...mouth(), ...ingots(), oreHeap(),
  ];
}

export const smelterV4 = defineModel({
  id: "smelter-v4",
  tiles: { w: 2, h: 2 },
  batchId: "polished",
  name: "Smelter (v4)",
  tags: ["building","smelter","low-poly","age-of-mcp","v4"],
  summary: "Low-poly smelter.",
  description: "A stone furnace that smelts ore into iron ingots.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench", position: [0, 0.12, 0.44], facing: Math.PI },
  ],
  interactions: [
    { id: "smelt", label: "Tending the furnace", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
